import { Alert, Button, Empty, Pagination, Select, Space, Spin, Timeline, Typography } from "antd"
import { useState } from "react"
import { siteWorkflowApi, workflowError } from "../../utils/siteWorkflowApi"
import { workflowRequest } from "../../utils/workflowTransport"
import type { WorkflowPage } from "../../types/siteWorkflows"
import { useWorkflowResource } from "./useWorkflowResource"
export function WorkflowError({ error, retry }: { error?: unknown; retry?: () => void }) {
  return error ? (
    <Alert
      type="error"
      showIcon
      message={workflowError(error)}
      action={retry && <Button onClick={retry}>Recharger</Button>}
    />
  ) : null
}
export function WorkflowPager({
  page,
  total,
  onChange,
}: {
  page: number
  total?: number
  onChange: (n: number) => void
}) {
  return (
    <Pagination
      size="small"
      current={page}
      total={total ?? 0}
      pageSize={20}
      showSizeChanger={false}
      onChange={onChange}
    />
  )
}
export function AssigneePicker({
  enterprise,
  value,
  onChange,
  required,
}: {
  enterprise: string
  value?: string | null
  onChange?: (id: string | null) => void
  required?: boolean
}) {
  const data = useWorkflowResource(`users/${enterprise}`, (signal) =>
    siteWorkflowApi.users(enterprise, signal),
  )
  return (
    <>
      <Select
        showSearch
        allowClear={!required}
        optionFilterProp="label"
        loading={data.loading}
        value={value ?? undefined}
        onChange={(v) => onChange?.(v ?? null)}
        style={{ width: "100%" }}
        placeholder="Responsable"
        options={(data.data ?? [])
          .filter((u) => u.IsActive !== false)
          .map((u) => ({
            value: u.UUID,
            label: `${u.FirstName} ${u.LastName}`.trim() || u.Username,
          }))}
      />
      <WorkflowError error={data.error} retry={data.reload} />
    </>
  )
}
export function WorkflowHistory({
  site,
  kind,
  id,
  refresh = 0,
}: {
  site: string
  kind: string
  id: string
  refresh?: number
}) {
  const [page, setPage] = useState(1)
  const data = useWorkflowResource(`events/${site}/${kind}/${id}/${page}/${refresh}`, (signal) =>
    siteWorkflowApi.events(site, kind, id, page, signal),
  )
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <WorkflowError error={data.error} retry={data.reload} />
      {data.loading ? (
        <Spin />
      ) : (
        <Timeline
          items={(data.data?.items ?? []).map((e) => ({
            children: (
              <>
                <Typography.Text type="secondary">
                  {new Date(e.createdAt).toLocaleString("fr-FR")}
                </Typography.Text>
                <div style={{ whiteSpace: "pre-wrap" }}>{e.reason}</div>
                <Typography.Text type="secondary">Auteur : {e.actorUuid}</Typography.Text>
              </>
            ),
          }))}
        />
      )}
      <WorkflowPager page={page} total={data.data?.total} onChange={setPage} />
    </Space>
  )
}
export function TargetPicker({
  site,
  asset,
  component,
  onAsset,
  onComponent,
  disabled,
}: {
  site: string
  asset: string | null
  component: string | null
  onAsset: (id: string | null) => void
  onComponent: (id: string | null) => void
  disabled?: boolean
}) {
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const assets = useWorkflowResource(`targets/${site}/${query}/${page}`, (signal) =>
    workflowRequest<WorkflowPage<{ uuid: string; name: string }>>(
      `physical-assets?physicalSiteUuid=${encodeURIComponent(site)}&q=${encodeURIComponent(query)}&limit=50&offset=${(page - 1) * 50}`,
      signal,
    ),
  )
  const components = useWorkflowResource(asset ? `components/${asset}` : "", (signal) =>
    workflowRequest<WorkflowPage<{ uuid: string; name: string }>>(
      `physical-components?physicalAssetUuid=${asset}&limit=200`,
      signal,
    ),
  )
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Select
        style={{ width: "100%" }}
        disabled={disabled}
        allowClear
        showSearch
        filterOption={false}
        placeholder="Site entier ou équipement"
        value={asset ?? undefined}
        onSearch={(q) => {
          setQuery(q)
          setPage(1)
        }}
        onChange={(v) => {
          onAsset(v ?? null)
          onComponent(null)
        }}
        loading={assets.loading}
        options={(assets.data?.items ?? []).map((a) => ({ value: a.uuid, label: a.name }))}
      />
      {(assets.data?.total ?? 0) > 50 && (
        <Pagination
          size="small"
          pageSize={50}
          total={assets.data?.total}
          current={page}
          onChange={setPage}
          showSizeChanger={false}
        />
      )}
      {asset && (
        <Select
          style={{ width: "100%" }}
          disabled={disabled}
          allowClear
          placeholder="Équipement entier ou composant"
          value={component ?? undefined}
          onChange={(v) => onComponent(v ?? null)}
          options={(components.data?.items ?? []).map((c) => ({ value: c.uuid, label: c.name }))}
        />
      )}
      <WorkflowError error={assets.error || components.error} />
    </Space>
  )
}
export const NoWorkflowItems = () => <Empty description="Aucun élément pour cette sélection." />
